import { Link } from "react-router-dom";
import { useQuery } from "convex/react";
import { useState } from "react";
import { api } from "../../convex/_generated/api";
import { formatRelativeTime } from "../lib/utils";
import { hasConvexConfig, hasWorkOsConfig } from "../lib/config";

const STAGES = ["", "drafting", "confirmed", "shipped", "received", "closed"] as const;

function normalizeStageLocal(status: string): string {
  const map: Record<string, string> = {
    draft: "drafting",
    active: "drafting",
    receiving: "shipped",
    completed: "closed",
    archived: "closed",
  };
  return map[status] ?? status;
}

export function OrdersHubPage() {
  if (!hasConvexConfig || !hasWorkOsConfig) {
    return (
      <div className="page">
        <h1>Orders</h1>
        <p className="lede">Configure WorkOS and Convex to manage purchase orders.</p>
      </div>
    );
  }
  return <OrdersAuthenticated />;
}

function OrdersAuthenticated() {
  const [status, setStatus] = useState<string>("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const data = useQuery(api.orders.listActive, {
    status: status
      ? (status as
          | "drafting"
          | "confirmed"
          | "shipped"
          | "received"
          | "closed"
          | "draft"
          | "active"
          | "receiving"
          | "completed"
          | "archived")
      : undefined,
    search: search || undefined,
    limit: 50,
  });

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Orders</h1>
          <p className="lede">Purchase orders by stage — confirm, ship, receive, send.</p>
        </div>
        <div className="header-actions">
          <Link className="btn primary" to="/orders/new">
            New order
          </Link>
        </div>
      </div>

      <div className="toolbar" role="search">
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && setSearch(searchInput.trim())}
          placeholder="Search orders…"
        />
        <button type="button" className="btn" onClick={() => setSearch(searchInput.trim())}>
          Search
        </button>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label="Stage filter"
        >
          {STAGES.map((s) => (
            <option key={s || "all"} value={s}>
              {s || "All stages"}
            </option>
          ))}
        </select>
      </div>

      {data === undefined ? (
        <p className="muted">Loading…</p>
      ) : (
        <>
          <section className="panel" style={{ padding: 0 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Stage</th>
                  <th>Supplier</th>
                  <th>Lines</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {data.active.map((o) => {
                  const stage =
                    "stage" in o && o.stage ? String(o.stage) : normalizeStageLocal(o.status);
                  return (
                    <tr key={o._id}>
                      <td>
                        <Link to={`/orders/${o._id}`}>{o.title}</Link>
                        {o.poNumber ? <div className="muted tiny">PO {o.poNumber}</div> : null}
                      </td>
                      <td>
                        <span className={`stage-chip ${stage}`}>{stage}</span>
                      </td>
                      <td>{o.supplier ?? "—"}</td>
                      <td>{o.rowCount}</td>
                      <td>{formatRelativeTime(o.lastActivityAt)}</td>
                    </tr>
                  );
                })}
                {data.active.length === 0 && (
                  <tr>
                    <td colSpan={5} className="muted">
                      No open orders.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>

          {data.completed.length > 0 && (
            <section className="panel" style={{ marginTop: "0.85rem", padding: 0 }}>
              <div style={{ padding: "0.75rem 1rem 0" }}>
                <h2 className="panel-title">Closed</h2>
              </div>
              <table className="data-table">
                <tbody>
                  {data.completed.map((o) => (
                    <tr key={o._id}>
                      <td>
                        <Link to={`/orders/${o._id}`}>{o.title}</Link>
                      </td>
                      <td>
                        <span className="stage-chip closed">closed</span>
                      </td>
                      <td>{formatRelativeTime(o.lastActivityAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}
    </div>
  );
}
